// ✅ src/components/Header.jsx
// ✅ Search bar shows for ALL sections (Structure / 6MM / Binding Wire / Rings)
// ✅ Category + Size dropdowns ONLY for Structure
// ✅ Hides Search entirely on /profile (including /profile/*) + /privacy-policy
// ✅ DOES NOT show section tabs in header (tabs stay on ProductsPage)
// ✅ Added Personal Creatives logo/button beside cart
// ✅ Opens /personal-creatives on click

import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { colors } from "../theme";
import { SHEETS } from "../utils/api";
import { usePolling } from "../utils/usePolling";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import CloseIcon from "@mui/icons-material/Close";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import PersonIcon from "@mui/icons-material/Person";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import LogoutIcon from "@mui/icons-material/Logout";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import Badge from "@mui/material/Badge";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import HelpFAQ from "../models/HelpFAQ";
import CartPopup from "./CartPopup";
import { toast } from "react-toastify";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import GroupsIcon from "@mui/icons-material/Groups";
import PrivacyTipOutlinedIcon from "@mui/icons-material/PrivacyTipOutlined";
import { useCreativesFilters } from "../contexts/CreativesFiltersContext";
import { clearAdminSession } from "../utils/adminView";

// ===== Creatives filter bar — rendered inside the yellow header on
// /personal-creatives and /promotional routes. State lives in
// CreativesFiltersContext so PromotionalPage and Header read/write the
// same values.
function CreativesFiltersBar({ variant }) {
  const ctx = useCreativesFilters();
  const wrapRef = useRef(null);

  // Local toggle for the date picker popover. Kept here (not in context)
  // so opening calendar on one route doesn't accidentally re-open on
  // another.
  const [openCal, setOpenCal] = useState(false);

  useEffect(() => {
    if (!openCal) return;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpenCal(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [openCal]);

  if (!ctx) return null;
  const {
    dateFilter,
    setDateFilter,
    nameFilter,
    setNameFilter,
    selectedFirm,
    setSelectedFirm,
    firms,
    customFooterText,
    setCustomFooterText,
    availableDates = [],
    // mobileInput / setMobileInput intentionally NOT destructured — the
    // mobile field has been removed from the header. PromotionalPage
    // still uses dealerMobile (from logged-in dealer) for the footer.
  } = ctx;

  const isFestival = variant === "Festival";
  const placeholder = isFestival
    ? "Search Festival"
    : "Search Category";

  const toInputDate = (d) => {
    if (!d) return "";
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return "";
    const y = dt.getFullYear();
    const mm = String(dt.getMonth() + 1).padStart(2, "0");
    const dd = String(dt.getDate()).padStart(2, "0");
    return `${y}-${mm}-${dd}`;
  };

  // Compact inline layout — single row on desktop, wraps onto multiple
  // lines on narrow viewports so all 4 inputs remain reachable on mobile
  // without the row growing horizontally past the screen edge.
  return (
    <div className="flex flex-wrap items-center gap-2 w-full">
      {/* Date */}
      <div className="relative shrink-0" ref={wrapRef}>
        <button
          type="button"
          onClick={() => setOpenCal((s) => !s)}
          className="h-9 px-3 rounded-lg border border-gray-300 bg-white outline-none font-semibold text-sm text-left flex items-center justify-between gap-2 min-w-[110px] hover:border-yellow-500"
          title="Filter by date"
        >
          <span className={dateFilter ? "text-gray-800" : "text-gray-500"}>
            {dateFilter
              ? (() => {
                  const [yy, mm, dd] = dateFilter.split("-");
                  return `${dd}/${mm}/${yy}`;
                })()
              : "Calendar"}
          </span>
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="text-gray-500 shrink-0"
          >
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
        </button>
        {openCal && (
          <div className="sota-cal-pop absolute left-0 top-full mt-2 z-[200] bg-white rounded-xl shadow-2xl border">
            {/* Calendar styling — yellow highlight for dates that have
                creatives, dark fill for the currently-selected date. */}
            <style>{`
              .sota-cal-pop .react-datepicker {
                border: 1px solid #e5e7eb;
                border-radius: 12px;
                box-shadow: 0 12px 28px rgba(0,0,0,0.15);
                font-family: inherit;
                overflow: hidden;
              }
              .sota-cal-pop .react-datepicker__triangle { display: none; }
              .sota-cal-pop .react-datepicker__header {
                background: #fff;
                border-bottom: 1px solid #f1f5f9;
                padding-top: 10px;
              }
              .sota-cal-pop .react-datepicker__current-month {
                font-weight: 700;
                color: #0f172a;
                margin-bottom: 6px;
              }
              .sota-cal-pop .react-datepicker__day-name,
              .sota-cal-pop .react-datepicker__day {
                width: 2rem;
                line-height: 2rem;
                margin: 2px;
                border-radius: 8px;
                font-size: 13px;
              }
              .sota-cal-pop .react-datepicker__day-name { color: #64748b; font-weight: 700; }
              .sota-cal-pop .sota-cal-day { color: #0f172a; }
              .sota-cal-pop .sota-cal-day:hover { background: #f1f5f9; }
              .sota-cal-pop .sota-cal-day-available {
                background: #fef3c7 !important;
                color: #92400e !important;
                font-weight: 800 !important;
                border: 1px solid #facc15 !important;
              }
              .sota-cal-pop .sota-cal-day-selected,
              .sota-cal-pop .react-datepicker__day--selected {
                background: #0f172a !important;
                color: #ffffff !important;
                font-weight: 800 !important;
                border: 1px solid #0f172a !important;
              }
              .sota-cal-pop .react-datepicker__day--keyboard-selected {
                background: transparent;
                color: inherit;
              }
            `}</style>
            <DatePicker
              selected={dateFilter ? new Date(dateFilter) : null}
              onChange={(date) => {
                setDateFilter(date ? toInputDate(date) : "");
                setOpenCal(false);
              }}
              inline
              dayClassName={(date) => {
                const key = toInputDate(date);
                if (dateFilter && key === dateFilter)
                  return "sota-cal-day sota-cal-day-selected";
                if (availableDates.includes(key))
                  return "sota-cal-day sota-cal-day-available";
                return "sota-cal-day";
              }}
            />
            {dateFilter && (
              <button
                type="button"
                onClick={() => {
                  setDateFilter("");
                  setOpenCal(false);
                }}
                className="w-full py-2 rounded-b-xl bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-semibold border-t border-gray-200"
              >
                Show all dates
              </button>
            )}
          </div>
        )}
      </div>

      {/* Search by name — compact (smaller than before so the new
          customize-text field has room next to it). */}
      <input
        type="text"
        value={nameFilter}
        onChange={(e) => setNameFilter(e.target.value)}
        placeholder={placeholder}
        title="Search creatives by name"
        className="h-9 flex-1 min-w-[110px] max-w-[170px] px-3 rounded-lg border border-gray-300 bg-white outline-none font-medium text-sm focus:border-yellow-500"
      />

      {/* Customize footer text — free-form. Whatever the dealer types
          here OVERRIDES the default FIRM-MOBILE footer line on the
          downloaded image. Empty = fall back to FIRM-MOBILE. */}
      <input
        type="text"
        value={customFooterText || ""}
        onChange={(e) => setCustomFooterText(e.target.value)}
        placeholder="Customise your footer text here "
        title="Type anything to print in the image footer (overrides firm-mobile)"
        className="h-9 flex-[2] min-w-[140px] px-3 rounded-lg border border-gray-300 bg-white outline-none font-medium text-sm focus:border-yellow-500"
      />

      {/* Firm Name — dropdown when dealer has multiple firms, READ-ONLY
          display when they have just one (so it can't be accidentally
          edited). Falls through to a plain editable input only if there
          are zero firms (very edge case). */}
      {firms.length > 1 ? (
        <select
          value={selectedFirm}
          onChange={(e) => setSelectedFirm(e.target.value)}
          className="h-9 px-2 rounded-lg border border-gray-300 bg-white outline-none font-semibold text-sm flex-[2] min-w-[130px] max-w-[200px] focus:border-yellow-500"
          title="Firm Name"
        >
          {firms.map((f) => (
            <option key={f} value={f}>
              {f}
            </option>
          ))}
        </select>
      ) : firms.length === 1 ? (
        <input
          type="text"
          value={selectedFirm}
          readOnly
          tabIndex={-1}
          aria-readonly="true"
          title="Firm Name (only one firm on file — not editable)"
          className="h-9 px-3 rounded-lg border border-gray-300 bg-gray-50 outline-none font-semibold text-sm text-gray-800 cursor-default flex-[2] min-w-[130px] max-w-[200px] select-text"
        />
      ) : (
        <input
          type="text"
          value={selectedFirm}
          onChange={(e) => setSelectedFirm(e.target.value)}
          placeholder="Firm"
          className="h-9 px-3 rounded-lg border border-gray-300 bg-white outline-none font-semibold text-sm flex-[2] min-w-[130px] max-w-[200px] focus:border-yellow-500"
          title="Firm Name"
        />
      )}

      {/* Mobile field is intentionally NOT rendered here — the footer on
          downloaded creatives always uses the logged-in dealer's phone
          (read automatically from localStorage). Keeping it out of the
          header reduces clutter and prevents accidental edits. */}

      {(dateFilter || nameFilter || customFooterText) && (
        <button
          type="button"
          onClick={() => {
            setDateFilter("");
            setNameFilter("");
            setCustomFooterText("");
          }}
          className="h-9 px-3 rounded-lg bg-white hover:bg-gray-100 text-gray-700 text-xs font-semibold border border-gray-300 shrink-0"
        >
          Clear
        </button>
      )}
    </div>
  );
}

const PRODUCTS_CACHE_KEY = "products_cache_v2";
const PRODUCTS_CACHE_TS_KEY = "products_cache_ts_v2";
const MASTER_CACHE_TTL = 30 * 1000; // 30 sec
const PRODUCTS_URL = SHEETS.products;

// Always return cached if it exists. Polling refresh karta rahega background me,
// to TTL-based eviction zaroori nahi — UI flicker avoid karne ke liye stale data
// dikhana better hai.
const getCachedProducts = () => {
  try {
    const raw = localStorage.getItem(PRODUCTS_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

const categories = [
  "All",
  "Angle",
  "Channel",
  "Flat",
  "Round",
  "Square",
  "Mix",
  "Rejected",
];

const typeMap = {
  All: null,
  Mix: "Mix",
  Angle: "A",
  Flat: "F",
  Round: "R",
  Square: "S",
  Channel: "C",
  Rejected: "Rejected",
};

const typeFullName = {
  A: "Angle",
  C: "Channel",
  F: "Flat",
  R: "Round",
  S: "Square",
  Mix: "Mix",
  Rejected: "Rejected",
};

const typeOrder = [
  "Angle",
  "Channel",
  "Flat",
  "Round",
  "Square",
  "Mix",
  "Rejected",
];

const searchShortcuts = {
  a: "Angle",
  f: "Flat",
  s: "Square",
  c: "Channel",
  r: "Round",
};

const categoryKeywordToType = {
  angle: "A",
  flat: "F",
  channel: "C",
  round: "R",
  square: "S",
  mix: "Mix",
  rejected: "Rejected",
};

const typeToCategory = {
  A: "Angle",
  F: "Flat",
  C: "Channel",
  R: "Round",
  S: "Square",
  Mix: "Mix",
  Rejected: "Rejected",
};

const safeArray = (v) => (Array.isArray(v) ? v : []);

export default function Header({
  searchQuery = "",
  setSearchQuery = () => {},
  cart = [],
  setCart = () => {},
  selectedCategory = "All",
  setSelectedCategory = () => {},
  selectedSize = "All",
  setSelectedSize = () => {},
  selectedRingSize = "All",
  setSelectedRingSize = () => {},
  ringSizeOptions = [],
  activeSection = "Structure",
}) {
  const [user, setUser] = useState(null);
  const [showHelpPopup, setShowHelpPopup] = useState(false);

  const [standardItems, setStandardItems] = useState([]);
  const [sizesByCategory, setSizesByCategory] = useState({});
  const [openCart, setOpenCart] = useState(false);

  const [openCategory, setOpenCategory] = useState(false);
  const [openSize, setOpenSize] = useState(false);

  const [openCategoryMobile, setOpenCategoryMobile] = useState(false);
  const [openSizeMobile, setOpenSizeMobile] = useState(false);

  const [showSuggestions, setShowSuggestions] = useState(false);
  const [mobileProfileOpen, setMobileProfileOpen] = useState(false);
  const [desktopProfileOpen, setDesktopProfileOpen] = useState(false);

  const [searchInput, setSearchInput] = useState(searchQuery || "");
  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery || "");

  const searchRef = useRef(null);
  const categoryRef = useRef(null);
  const sizeRef = useRef(null);
  const profileRef = useRef(null);
  const categoryMobileRef = useRef(null);
  const sizeMobileRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();

  const headerTitle = useMemo(() => {
    const section = String(activeSection || "")
      .trim()
      .toLowerCase();

    if (section === "structure") return "Steel Order";
    if (section === "6mm" || section === "tmt") return "TMT Order";
    if (section === "binding wire" || section === "bindingwire")
      return "Binding Wire";
    if (section === "rings" || section === "ring") return "Rings";

    return "Order";
  }, [activeSection]);

  const hideSearchByRoute = useMemo(() => {
    const path = location.pathname || "";

    return (
      path === "/profile" ||
      path.startsWith("/profile/") ||
      path === "/privacy-policy" ||
      // ✅ ADD THIS LINE
      path === "/personal-creatives" ||
      path.startsWith("/personal-creatives/") ||
      path === "/promotional" ||
      path.startsWith("/promotional/")
    );
  }, [location.pathname]);

  // Detect creatives routes — when active, render the CreativesFiltersBar
  // (Date / Name / Firm / Mobile) as a SECOND ROW inside the yellow header.
  const creativesVariant = useMemo(() => {
    const path = location.pathname || "";
    if (path === "/personal-creatives" || path.startsWith("/personal-creatives/"))
      return "Festival";
    if (path === "/promotional" || path.startsWith("/promotional/"))
      return "Promotional";
    return null;
  }, [location.pathname]);

  // ✅ "Order Here" page = products dashboard with a section actually picked.
  //    Search bar + cart sirf yahi dikhne chahiye (welcome dashboard pe nahi,
  //    aur baaki routes jaise vyapar-sales / marketing-collators pe bhi nahi).
  const isOrderHere = useMemo(() => {
    const path = location.pathname || "";
    return path === "/dashboard" && !!String(activeSection || "").trim();
  }, [location.pathname, activeSection]);

  // ✅ Section filters
  const isStructure = activeSection === "Structure";
  const isRings = activeSection === "Rings";
  const showStructureFilters = isStructure;
  const showRingFilters = isRings;

  useEffect(() => {
    const dealer = JSON.parse(localStorage.getItem("dealer"));
    if (dealer) setUser(dealer);
    else navigate("/");
  }, [navigate]);

  useEffect(() => {
    setSearchInput(searchQuery || "");
  }, [searchQuery]);

  const buildFromRows = useCallback((itemsArray) => {
    const byCategory = { All: new Set() };
    const items = [];
    const seen = new Set();

    (itemsArray || []).forEach((row) => {
      const stdName = String(row["Standard Name"] || "").trim();
      const rowType = String(row["TYPE"] || "").trim();
      if (!stdName) return;

      byCategory.All.add(stdName);

      const key = `${rowType}||${stdName}`;
      if (!seen.has(key)) {
        seen.add(key);
        items.push({ name: stdName, type: rowType });
      }

      Object.entries(typeMap).forEach(([cat, code]) => {
        if (!code) return;
        if (rowType === code) {
          if (!byCategory[cat]) byCategory[cat] = new Set();
          byCategory[cat].add(stdName);
        }
      });
    });

    const normalized = {};
    Object.keys(byCategory).forEach((cat) => {
      normalized[cat] = [...byCategory[cat]].sort();
    });

    setSizesByCategory(normalized);
    setStandardItems(items);
  }, []);

  const loadProducts = useCallback(
    async (opts = {}) => {
      try {
        if (!opts.skipCache) {
          const cached = getCachedProducts();
          if (cached?.length) {
            buildFromRows(cached);
            if (!opts.refreshAfterCache) return;
          }
        }

        const res = await fetch(PRODUCTS_URL, { cache: "no-store" });
        if (!res.ok) return;
        const data = await res.json();

        localStorage.setItem(PRODUCTS_CACHE_KEY, JSON.stringify(data || []));
        localStorage.setItem(PRODUCTS_CACHE_TS_KEY, String(Date.now()));

        buildFromRows(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error(err);
      }
    },
    [buildFromRows]
  );

  // ✅ Fetch sizes only when Structure — show cached immediately, then refresh
  useEffect(() => {
    if (!isStructure) return;
    loadProducts({ refreshAfterCache: true });
  }, [isStructure, loadProducts]);

  // Auto-poll products every 30s while Structure view is active, so new
  // sizes / renamed items appear in the header dropdown without a page refresh.
  usePolling(
    () => loadProducts({ skipCache: true }),
    30_000,
    isStructure
  );

  useEffect(() => {
    if (!isStructure) return;

    const allowedSizes = sizesByCategory[selectedCategory] ||
      sizesByCategory.All || ["All"];

    if (!allowedSizes.includes(selectedSize) && selectedSize !== "All") {
      setSelectedSize("All");
    }
  }, [
    isStructure,
    selectedCategory,
    sizesByCategory,
    selectedSize,
    setSelectedSize,
  ]);

  useEffect(() => {
    if (!isRings) return;
    const allowed = ["All", ...safeArray(ringSizeOptions)];
    if (!allowed.includes(selectedRingSize)) {
      setSelectedRingSize("All");
    }
  }, [isRings, ringSizeOptions, selectedRingSize, setSelectedRingSize]);

  const sizesForCategory = useMemo(() => {
    return sizesByCategory[selectedCategory] || sizesByCategory.All || ["All"];
  }, [sizesByCategory, selectedCategory]);

  const stripTypePrefix = (name, type) => {
    const t = String(type || "").trim();
    if (!t) return name;
    const prefix = `${t} `;
    return name.startsWith(prefix) ? name.slice(prefix.length) : name;
  };

  const buildDisplayName = (name, type) => {
    const full = typeFullName[type] || "All";
    const cleaned = stripTypePrefix(name, type);
    return full !== "All" ? `${full} ${cleaned}` : cleaned;
  };

  const preparedItems = useMemo(() => {
    return standardItems.map((it) => {
      const name = String(it.name || "");
      const type = String(it.type || "");
      const cleaned = stripTypePrefix(name, type);
      return {
        ...it,
        _lc: name.toLowerCase(),
        _clean: cleaned,
        _cleanLc: cleaned.toLowerCase(),
      };
    });
  }, [standardItems]);

  // ✅ Debounce ALWAYS pushes searchQuery (all sections)
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setSearchQuery(searchInput);
    }, 150);
    return () => clearTimeout(t);
  }, [searchInput, setSearchQuery]);

  const filteredSuggestions = useMemo(() => {
    if (!isStructure) return [];

    const qRaw = String(debouncedSearch || "").trim();
    const q = qRaw.toLowerCase();
    if (!q) return [];

    if (q.length === 1 && searchShortcuts[q]) {
      const cat = searchShortcuts[q];
      const code = typeMap[cat];

      return (sizesByCategory[cat] || [])
        .filter((s) => s && s !== "All")
        .slice(0, 200)
        .map((name) => ({
          label: buildDisplayName(name, code),
          size: name,
          category: cat,
          type: code,
        }));
    }

    const parts = q.split(/\s+/).filter(Boolean);
    const first = parts[0];
    const rest = parts.slice(1).join(" ");

    const keywords = Object.keys(categoryKeywordToType);

    let matchedKeyword = null;
    if (categoryKeywordToType[first]) matchedKeyword = first;
    else if (first.length >= 2)
      matchedKeyword = keywords.find((k) => k.startsWith(first)) || null;

    const typeCode = matchedKeyword
      ? categoryKeywordToType[matchedKeyword]
      : null;

    if (typeCode) {
      const cat = typeToCategory[typeCode];

      let list = (sizesByCategory[cat] || []).filter((s) => s && s !== "All");
      if (rest) {
        const restLc = rest.toLowerCase();
        list = list.filter((name) =>
          String(name).toLowerCase().includes(restLc),
        );
      }

      return list.slice(0, 250).map((name) => ({
        label: buildDisplayName(name, typeCode),
        size: name,
        category: cat,
        type: typeCode,
      }));
    }

    return preparedItems
      .filter((it) => it._lc.includes(q) || it._cleanLc.includes(q))
      .map((it) => {
        const category = typeToCategory[it.type] || "All";
        return {
          label: category !== "All" ? `${category} ${it._clean}` : it._clean,
          size: it.name,
          category,
          type: it.type,
        };
      })
      .sort((a, b) => {
        const ai = typeOrder.indexOf(a.category);
        const bi = typeOrder.indexOf(b.category);
        if (ai !== bi) return ai - bi;
        return a.label.localeCompare(b.label);
      })
      .slice(0, 80);
  }, [debouncedSearch, preparedItems, sizesByCategory, isStructure]);

  const handleSearchChange = (value) => {
    setSearchInput(value);

    // ✅ only reset dropdowns when Structure / Rings
    if (value.trim() !== "") {
      if (isStructure) {
        if (selectedCategory !== "All") setSelectedCategory("All");
        if (selectedSize !== "All") setSelectedSize("All");
        setOpenCategory(false);
        setOpenSize(false);
        setOpenCategoryMobile(false);
        setOpenSizeMobile(false);
      }

      if (isRings && selectedRingSize !== "All") {
        setSelectedRingSize("All");
        setOpenSize(false);
        setOpenSizeMobile(false);
      }
    }
  };

  const handleSuggestionClick = (item) => {
    setSearchInput("");
    setSearchQuery("");
    setSelectedCategory(item.category || "All");
    setSelectedSize(item.size || "All");

    setShowSuggestions(false);
    setOpenCategory(false);
    setOpenSize(false);
    setOpenCategoryMobile(false);
    setOpenSizeMobile(false);
  };

  const handleClickOutside = (e) => {
    if (searchRef.current && !searchRef.current.contains(e.target))
      setShowSuggestions(false);
    if (categoryRef.current && !categoryRef.current.contains(e.target))
      setOpenCategory(false);
    if (sizeRef.current && !sizeRef.current.contains(e.target))
      setOpenSize(false);
    if (profileRef.current && !profileRef.current.contains(e.target))
      setDesktopProfileOpen(false);

    if (
      categoryMobileRef.current &&
      !categoryMobileRef.current.contains(e.target)
    ) {
      setOpenCategoryMobile(false);
    }
    if (sizeMobileRef.current && !sizeMobileRef.current.contains(e.target)) {
      setOpenSizeMobile(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = () => {
    // Cart aur sheet caches ko PRESERVE karo across logout/session-expire
    // — sirf auth/session ki keys clear karo. User manually remove kare to hi cart se hatega.
    localStorage.removeItem("dealer");
    localStorage.removeItem("role");
    localStorage.removeItem("loginAt");
    clearAdminSession();
    // Note: NOT touching: "cart", "products_cache_*", "rings_cache_*",
    // "users_cache_*", "personal_creatives_cache_*", "firmsCache:*"
    navigate("/");
    toast.success("Logged out successfully");
  };

  const handleClearFilters = () => {
    setSearchInput("");
    setSearchQuery("");

    // ✅ Only reset category/size when Structure
    setSelectedCategory("All");
    setSelectedSize("All");

    setShowSuggestions(false);
    setOpenCategory(false);
    setOpenSize(false);
    setOpenCategoryMobile(false);
    setOpenSizeMobile(false);
  };

  const handleHomeClick = () => {
    handleClearFilters();
    navigate("/dashboard");
  };

  const handlePersonalCreativesClick = () => {
    navigate("/personal-creatives");
  };

  const handlePromotionalClick = () => {
    navigate("/promotional");
  };

  const selectCategory = (cat) => {
    setSelectedCategory(cat);
    setSelectedSize("All");

    setOpenCategory(false);
    setOpenSize(false);

    setOpenCategoryMobile(false);
    setTimeout(() => setOpenSizeMobile(true), 0);
  };

  if (!user) return null;

  const displayName = user?.name || user?.["Contact Person Name"] || "User";
  const displayPhone = user?.phone || user?.["Contact No"] || "";
  const isAdminUser = String(user?.type || "").toLowerCase() === "admin";

  // Profile completion % for the avatar ring. Prefer the value the Profile
  // page computed (it includes KYC); fall back to a profile-fields estimate
  // (matches the Profile calc when KYC isn't done yet — 5 profile + 12 KYC).
  const _profileFilled = [
    user?.name || user?.["Contact Person Name"],
    displayPhone,
    user?.firmName || user?.["Company Name"],
    user?.address || user?.["Address"],
    user?.email || user?.["Email Id"],
  ].filter((v) => String(v || "").trim()).length;
  const _cachedCompletion = Number(localStorage.getItem("profileCompletion"));
  const completionPct =
    Number.isFinite(_cachedCompletion) && _cachedCompletion > 0
      ? _cachedCompletion
      : Math.round((_profileFilled / 17) * 100);

  // Avatar wrapped in a completion ring, with the % shown below it.
  const AvatarWithRing = ({ letter = "U", percent = 0, avatarSize = 64, onClick }) => {
    const ringSize = avatarSize + 14;
    const stroke = 4;
    const r = ringSize / 2 - stroke / 2;
    const c = r * 2 * Math.PI;
    const clamped = Math.max(0, Math.min(100, Number(percent) || 0));
    const offset = c - (clamped / 100) * c;
    return (
      <div
        onClick={onClick}
        title={onClick ? "Open profile" : undefined}
        className={`flex flex-col items-center gap-1 shrink-0 ${
          onClick ? "cursor-pointer hover:opacity-90 active:scale-95 transition" : ""
        }`}
      >
        <div className="relative" style={{ width: ringSize, height: ringSize }}>
          <svg
            width={ringSize}
            height={ringSize}
            className="rotate-[-90deg] absolute inset-0"
          >
            <circle
              stroke="currentColor"
              className="text-gray-200"
              fill="transparent"
              strokeWidth={stroke}
              r={r}
              cx={ringSize / 2}
              cy={ringSize / 2}
            />
            <circle
              stroke="currentColor"
              className="text-yellow-500"
              fill="transparent"
              strokeWidth={stroke}
              strokeLinecap="round"
              strokeDasharray={`${c} ${c}`}
              style={{ strokeDashoffset: offset, transition: "stroke-dashoffset .5s ease" }}
              r={r}
              cx={ringSize / 2}
              cy={ringSize / 2}
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <div
              className="rounded-full bg-gradient-to-br from-yellow-500 to-orange-600 text-white flex items-center justify-center font-bold shadow-lg"
              style={{ width: avatarSize, height: avatarSize, fontSize: avatarSize * 0.4 }}
            >
              {letter}
            </div>
          </div>
        </div>
        <span className="text-[11px] font-extrabold text-yellow-600 leading-none">
          {clamped}%
        </span>
      </div>
    );
  };

  // ✅ Search + cart sirf "Order Here" page pe (products dashboard, section picked)
  const showSearch = !hideSearchByRoute && isOrderHere;

  return (
    <>
      {/* ===================== DESKTOP HEADER ===================== */}
      <motion.header
        className="fixed top-0 left-0 w-full z-50 hidden md:block"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        style={{
          background: colors.yellow,
          padding: "14px 30px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
        }}
      >
        <div className="mx-auto flex items-center justify-between gap-6">
          {/* Logo */}
          <div className="flex items-center gap-4">
            <button onClick={handleHomeClick}>
              <motion.div
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                <img
                  src="/images/Logo_Thumbnail-removebg.png"
                  alt="Friends Steel"
                  className="h-14 drop-shadow-md"
                />
              </motion.div>
            </button>

            <div className="h-14 w-px bg-gray-700 opacity-50 hidden sm:block" />

            {/* Heartbeat keyframes — gently pulse the two desktop nav
                links so the user's eye finds them. Second link starts
                slightly out of phase so they don't beat in lockstep. */}
            <style>{`
              @keyframes sotaHeaderHeartbeat {
                0%, 100% { transform: scale(1);    }
                20%      { transform: scale(1.06); }
                40%      { transform: scale(1);    }
                60%      { transform: scale(1.03); }
              }
              .sota-header-heartbeat {
                display: inline-block;
                animation: sotaHeaderHeartbeat 1.8s ease-in-out infinite;
                transform-origin: center;
                will-change: transform;
              }
              .sota-header-heartbeat-2 { animation-delay: 0.4s; }
              @media (prefers-reduced-motion: reduce) {
                .sota-header-heartbeat { animation: none; }
              }
            `}</style>

            <div className="hidden lg:flex items-center gap-6 xl:gap-8">
              <button onClick={handlePersonalCreativesClick}>
                <motion.h3
                  className="sota-header-heartbeat text-xl xl:text-2xl 2xl:text-3xl font-semibold tracking-wide underline cursor-pointer"
                  style={{
                    color: colors.darkGrey,
                    fontFamily: "'DM-sans', serif",
                  }}
                  whileHover={{ scale: 1.08 }}
                >
                  Festive Greetings
                </motion.h3>
              </button>
              <span className="text-gray-400 text-xl">|</span>
              <button onClick={handlePromotionalClick}>
                <motion.h3
                  className="sota-header-heartbeat sota-header-heartbeat-2 text-xl xl:text-2xl 2xl:text-3xl font-semibold tracking-wide underline cursor-pointer"
                  style={{
                    color: colors.darkGrey,
                    fontFamily: "'DM-sans', serif",
                  }}
                  whileHover={{ scale: 1.08 }}
                >
                  Promotional Poster
                </motion.h3>
              </button>
            </div>
          </div>

          {/* Inline creatives filter bar — sits in the SAME row as the
              header links + cart + profile so the header height doesn't
              grow on /personal-creatives or /promotional pages. */}
          {creativesVariant && (
            <div className="flex-1 max-w-4xl">
              <CreativesFiltersBar variant={creativesVariant} />
            </div>
          )}

          {/* ✅ Search Bar (ALL sections) */}
          {showSearch && (
            <div className="flex-1 max-w-3xl" ref={searchRef}>
              <div className="flex items-center bg-white rounded-xl shadow-lg">
                {/* ✅ Category + Size ONLY for Structure */}
                {showStructureFilters && (
                  <div className="flex">
                    {/* Category dropdown */}
                    <div className="relative" ref={categoryRef}>
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => {
                          setOpenCategory((prev) => !prev);
                          setOpenSize(false);
                        }}
                        className="flex items-center gap-2 px-6 py-3.5 font-bold text-gray-800 rounded-l-xl border-r border-gray-300"
                      >
                        {selectedCategory}
                        <ArrowDropDownIcon sx={{ fontSize: 20 }} />
                      </motion.button>

                      <AnimatePresence>
                        {openCategory && (
                          <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="absolute top-full mt-2 left-0 w-64 bg-white rounded-2xl shadow-2xl border border-gray-200 z-50"
                          >
                            {categories.map((cat) => (
                              <button
                                key={cat}
                                onClick={() => selectCategory(cat)}
                                className="w-full text-left px-6 py-4 hover:bg-yellow-50 font-medium"
                              >
                                {cat}
                              </button>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>

                    {/* Size dropdown */}
                    <div className="relative" ref={sizeRef}>
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => {
                          setOpenSize((prev) => !prev);
                          setOpenCategory(false);
                        }}
                        className="flex items-center gap-2 px-6 py-3.5 font-bold text-gray-800 border-r border-gray-300"
                      >
                        {selectedSize === "All" ? "Size" : selectedSize}
                        <ArrowDropDownIcon sx={{ fontSize: 20 }} />
                      </motion.button>

                      <AnimatePresence>
                        {openSize && (
                          <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="absolute top-full mt-2 left-0 w-64 bg-white rounded-2xl shadow-2xl border border-gray-200 z-50 max-h-96 overflow-y-auto"
                          >
                            <button
                              onClick={() => {
                                setSelectedSize("All");
                                setOpenSize(false);
                              }}
                              className="w-full text-left px-6 py-3 hover:bg-yellow-50 font-medium border-b"
                            >
                              All
                            </button>

                            {sizesForCategory
                              .filter((s) => s !== "All")
                              .map((size) => (
                                <button
                                  key={size}
                                  onClick={() => {
                                    setSelectedSize(size);
                                    setOpenSize(false);
                                  }}
                                  className="w-full text-left px-6 py-3 hover:bg-yellow-50 font-medium border-b last:border-0"
                                >
                                  {size}
                                </button>
                              ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>
                )}

                {showRingFilters && (
                  <div className="flex">
                    <div className="relative" ref={sizeRef}>
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => {
                          setOpenSize((prev) => !prev);
                          setOpenCategory(false);
                        }}
                        className="flex items-center gap-2 px-6 py-3.5 font-bold text-gray-800 rounded-l-xl border-r border-gray-300"
                      >
                        {selectedRingSize === "All"
                          ? "Ring Size"
                          : selectedRingSize}
                        <ArrowDropDownIcon sx={{ fontSize: 20 }} />
                      </motion.button>

                      <AnimatePresence>
                        {openSize && (
                          <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="absolute top-full mt-2 left-0 w-72 bg-white rounded-2xl shadow-2xl border border-gray-200 z-50 max-h-96 overflow-y-auto"
                          >
                            <button
                              onClick={() => {
                                setSelectedRingSize("All");
                                setOpenSize(false);
                              }}
                              className="w-full text-left px-6 py-3 hover:bg-yellow-50 font-medium border-b"
                            >
                              All
                            </button>

                            {safeArray(ringSizeOptions).map((size) => (
                              <button
                                key={size}
                                onClick={() => {
                                  setSelectedRingSize(size);
                                  setOpenSize(false);
                                }}
                                className="w-full text-left px-6 py-3 hover:bg-yellow-50 font-medium border-b last:border-0"
                              >
                                {size}
                              </button>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>
                )}

                {/* Search input */}
                <div className="relative flex-1">
                  <SearchRoundedIcon
                    sx={{
                      position: "absolute",
                      left: "16px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: colors.darkGrey,
                      fontSize: 26,
                    }}
                  />

                  <input
                    type="text"
                    placeholder={
                      isStructure ? "Search products..." : "Search items..."
                    }
                    value={searchInput}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    onFocus={() => isStructure && setShowSuggestions(true)}
                    className={`w-full pl-14 pr-20 py-3.5 outline-none text-gray-800 font-medium ${
                      isStructure || isRings ? "rounded-r-xl" : "rounded-xl"
                    }`}
                  />

                  {searchInput.trim() !== "" && (
                    <button
                      onClick={handleClearFilters}
                      className="absolute right-3 top-1/2 -translate-y-1/2 bg-gray-200 hover:bg-gray-300 text-gray-700 text-xs font-semibold px-3 py-1 rounded-full transition-all"
                      style={{ zIndex: 20 }}
                    >
                      Clear
                    </button>
                  )}

                  <AnimatePresence>
                    {isStructure &&
                      showSuggestions &&
                      searchInput.trim() &&
                      filteredSuggestions.length > 0 && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-2xl border max-h-96 overflow-y-auto z-40"
                        >
                          {filteredSuggestions.map((item) => (
                            <div
                              key={`${item.type}-${item.size}`}
                              onMouseDown={() => handleSuggestionClick(item)}
                              className="px-6 py-4 cursor-pointer hover:bg-yellow-50 font-medium border-b last:border-0"
                            >
                              {item.label}
                            </div>
                          ))}
                        </motion.div>
                      )}
                  </AnimatePresence>
                </div>
              </div>
            </div>
          )}

          {/* Desktop Icons */}
          <div className="flex items-center gap-4">
            {isOrderHere && (
            <button
              onClick={() => setOpenCart(true)}
              className="p-3 bg-white rounded-full shadow-lg"
            >
              <Badge badgeContent={cart.length} color="error">
                <ShoppingCartOutlinedIcon
                  sx={{ fontSize: 28, color: colors.darkGrey }}
                />
              </Badge>
            </button>
            )}

            {/* <button
              onClick={handlePersonalCreativesClick}
              className="p-3 bg-white rounded-full shadow-lg"
              title="Personal Creatives"
            >
              <AutoAwesomeIcon sx={{ fontSize: 28, color: colors.darkGrey }} />
            </button> */}

            <div className="relative" ref={profileRef}>
              <button
                onClick={() => setDesktopProfileOpen(!desktopProfileOpen)}
                className="flex items-center gap-3 bg-white rounded-full px-5 py-2 shadow-lg border border-gray-200"
              >
                <div className="w-11 h-11 rounded-full bg-gradient-to-br from-yellow-500 to-orange-600 text-white flex items-center justify-center text-xl font-bold">
                  {displayName?.[0] || "U"}
                </div>
                <ArrowDropDownIcon sx={{ color: colors.darkGrey }} />
              </button>

              <AnimatePresence>
                {desktopProfileOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -15 }}
                    className="absolute right-0 mt-4 w-80 bg-white rounded-2xl shadow-2xl border z-50"
                  >
                    <div className="flex items-center gap-4 p-6 border-b">
                      {isAdminUser ? (
                        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-yellow-500 to-orange-600 text-white flex items-center justify-center text-2xl font-bold shadow-lg">
                          {displayName?.[0] || "U"}
                        </div>
                      ) : (
                        <AvatarWithRing
                          letter={displayName?.[0] || "U"}
                          percent={completionPct}
                          avatarSize={64}
                          onClick={() => {
                            navigate("/profile");
                            setDesktopProfileOpen(false);
                          }}
                        />
                      )}
                      <div
                        onClick={() => {
                          navigate("/profile");
                          setDesktopProfileOpen(false);
                        }}
                        className="cursor-pointer"
                      >
                        <h4 className="font-bold text-lg">{displayName}</h4>
                        <p className="text-sm text-gray-600">
                          +91 {displayPhone}
                        </p>
                      </div>
                    </div>

                    {/* Profile menu: My Profile, Help & FAQ, Privacy Policy, Logout */}
                    <div className="p-4 space-y-2">
                      <button
                        onClick={() => {
                          navigate("/profile");
                          setDesktopProfileOpen(false);
                        }}
                        className="w-full text-left py-3 px-4 hover:bg-gray-100 rounded-lg flex items-center gap-3"
                      >
                        <PersonIcon /> My Profile
                      </button>

                      <button
                        onClick={() => {
                          setShowHelpPopup(true);
                          setDesktopProfileOpen(false);
                        }}
                        className="w-full text-left py-3 px-4 hover:bg-gray-100 rounded-lg flex items-center gap-3"
                      >
                        <HelpOutlineIcon /> Help & FAQ
                      </button>

                      <button
                        onClick={() => {
                          navigate("/privacy-policy");
                          setDesktopProfileOpen(false);
                        }}
                        className="w-full text-left py-3 px-4 hover:bg-gray-100 rounded-lg flex items-center gap-3"
                      >
                        <PrivacyTipOutlinedIcon /> Privacy Policy
                      </button>

                      <button
                        onClick={handleLogout}
                        className="w-full text-left py-3 px-4 hover:bg-red-50 text-red-600 font-bold rounded-lg flex items-center gap-3"
                      >
                        <LogoutIcon /> Logout
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </motion.header>

      {/* ===================== MOBILE HEADER ===================== */}
      <div
        className="fixed top-0 left-0 right-0 z-[60] md:hidden"
        style={{
          background: colors.yellow,
          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
        }}
      >
        <div className="px-4 pt-3 pb-4">
          {/* Outer container is a div — having a <button> wrap other
              <button> elements is invalid HTML and crashes hydration. The
              home-click handler now lives only on the logo portion. */}
          <div className="flex items-center gap-3">
            <button
              onClick={handleHomeClick}
              className="flex items-center gap-3 shrink-0"
            >
              <img
                src="/images/Logo_Thumbnail-removebg.png"
                alt="Logo"
                className="h-6"
              />
              <div className="h-5 w-px bg-gray-700 opacity-50" />
            </button>
            <div className="flex items-center gap-1">
              <button onClick={handlePersonalCreativesClick}>
                <motion.h3
                  className="sota-header-heartbeat text-[11px] sm:text-[12px] font-semibold tracking-wider underline cursor-pointer"
                  style={{
                    color: colors.darkGrey,
                    fontFamily: "'DM-sans', serif",
                  }}
                  whileHover={{ scale: 1.04 }}
                >
                  Festive Greetings Poster
                </motion.h3>
              </button>
              <span className="text-gray-400">|</span>
              <button onClick={handlePromotionalClick}>
                <motion.h3
                  className="sota-header-heartbeat sota-header-heartbeat-2 text-[11px] sm:text-[12px] font-semibold tracking-wider underline cursor-pointer"
                  style={{
                    color: colors.darkGrey,
                    fontFamily: "'DM-sans', serif",
                  }}
                  whileHover={{ scale: 1.04 }}
                >
                  Promotional Poster
                </motion.h3>
              </button>
            </div>
          </div>

          {/* Mobile creatives filter bar — rendered OUTSIDE the showSearch
              block because creatives routes hide the regular product search. */}
          {creativesVariant && (
            <div className="mt-3">
              <CreativesFiltersBar variant={creativesVariant} />
            </div>
          )}

          {/* ✅ Search ALWAYS (unless profile/privacy) */}
          {showSearch && (
            <>
              <div className="mt-3 relative" ref={searchRef}>
                <SearchRoundedIcon className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-600 text-2xl" />
                <input
                  type="text"
                  placeholder={
                    isStructure ? "Search products..." : "Search items..."
                  }
                  value={searchInput}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  onFocus={() => isStructure && setShowSuggestions(true)}
                  className="w-full pl-12 pr-20 py-1 bg-white rounded-xl text-base outline-none font-medium shadow-md"
                />

                {searchInput.trim() !== "" && (
                  <button
                    onClick={handleClearFilters}
                    className="absolute right-3 top-1/2 -translate-y-1/2 bg-gray-200 hover:bg-gray-300 text-gray-700 text-xs font-semibold px-3 py-1 rounded-full transition-all"
                    style={{ zIndex: 20 }}
                  >
                    Clear
                  </button>
                )}

                <AnimatePresence>
                  {isStructure &&
                    showSuggestions &&
                    searchInput.trim() &&
                    filteredSuggestions.length > 0 && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-2xl border z-[70] max-h-72 overflow-y-auto"
                      >
                        {filteredSuggestions.map((item) => (
                          <div
                            key={`${item.type}-${item.size}`}
                            onMouseDown={() => handleSuggestionClick(item)}
                            className="px-6 py-2 cursor-pointer hover:bg-yellow-50 font-medium border-b last:border-0"
                          >
                            {item.label}
                          </div>
                        ))}
                      </motion.div>
                    )}
                </AnimatePresence>
              </div>

              {/* ✅ Category + Size ONLY for Structure */}
              {showStructureFilters && (
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <div className="relative" ref={categoryMobileRef}>
                    <button
                      onClick={() => {
                        setOpenCategoryMobile((p) => !p);
                        setOpenSizeMobile(false);
                      }}
                      className="w-full bg-white rounded-xl px-4 py-1 shadow-md flex items-center justify-between font-semibold"
                    >
                      <span>{selectedCategory}</span>
                      <ArrowDropDownIcon />
                    </button>

                    <AnimatePresence>
                      {openCategoryMobile && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          className="absolute top-full mt-2 left-0 right-0 bg-white rounded-2xl shadow-2xl border z-[70] overflow-hidden"
                        >
                          {categories.map((cat) => (
                            <button
                              key={cat}
                              onClick={() => selectCategory(cat)}
                              className="w-full text-left px-5 py-2 hover:bg-yellow-50 font-medium border-b last:border-0"
                            >
                              {cat}
                            </button>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  <div className="relative" ref={sizeMobileRef}>
                    <button
                      onClick={() => {
                        setOpenSizeMobile((p) => !p);
                        setOpenCategoryMobile(false);
                      }}
                      className="w-full bg-white rounded-xl px-4 py-1 shadow-md flex items-center justify-between font-semibold"
                    >
                      <span>
                        {selectedSize === "All" ? "Size (All)" : selectedSize}
                      </span>
                      <ArrowDropDownIcon />
                    </button>

                    <AnimatePresence>
                      {openSizeMobile && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          className="absolute top-full mt-2 left-0 right-0 bg-white rounded-2xl shadow-2xl border z-[70] max-h-72 overflow-y-auto"
                        >
                          <button
                            onClick={() => {
                              setSelectedSize("All");
                              setOpenSizeMobile(false);
                            }}
                            className="w-full text-left px-5 py-2 hover:bg-yellow-50 font-medium border-b"
                          >
                            All
                          </button>

                          {sizesForCategory
                            .filter((s) => s && s !== "All")
                            .map((size) => (
                              <button
                                key={size}
                                onClick={() => {
                                  setSelectedSize(size);
                                  setOpenSizeMobile(false);
                                }}
                                className="w-full text-left px-5 py-2 hover:bg-yellow-50 font-medium border-b last:border-0"
                              >
                                {size}
                              </button>
                            ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ===================== MOBILE BOTTOM NAV ===================== */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t z-40 md:hidden shadow-2xl">
        <div className="flex justify-around items-center h-16">
          {isOrderHere && (
          <button
            onClick={() => setOpenCart(true)}
            className="relative flex flex-col items-center gap-1 py-2 px-4 text-gray-700"
          >
            <ShoppingCartOutlinedIcon sx={{ fontSize: 28 }} />
            <span className="text-xs font-medium">Cart</span>
            {cart.length > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                {cart.length}
              </span>
            )}
          </button>
          )}

          {/* <button
            onClick={handlePersonalCreativesClick}
            className="flex flex-col items-center gap-1 py-2 px-4 text-gray-700"
          >
            <AutoAwesomeIcon sx={{ fontSize: 28 }} />
            <span className="text-xs font-medium">Creatives</span>
          </button> */}

          <button
            onClick={() => setMobileProfileOpen(!mobileProfileOpen)}
            className="flex flex-col items-center gap-1 py-2 px-4 text-gray-700"
          >
            <PersonIcon sx={{ fontSize: 28 }} />
            <span className="text-xs font-medium">Profile</span>
          </button>
        </div>
      </div>

      {/* Mobile Profile Popup */}
      <AnimatePresence>
        {mobileProfileOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="fixed left-4 right-4 bottom-[calc(env(safe-area-inset-bottom)+5rem)] bg-white rounded-2xl shadow-2xl border z-[9999]"
          >
            <div className="flex items-center justify-between p-5 border-b">
              <div className="flex items-center gap-4">
                {isAdminUser ? (
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-yellow-500 to-orange-600 text-white flex items-center justify-center text-2xl font-bold">
                    {displayName?.[0] || "U"}
                  </div>
                ) : (
                  <AvatarWithRing
                    letter={displayName?.[0] || "U"}
                    percent={completionPct}
                    avatarSize={56}
                    onClick={() => {
                      navigate("/profile");
                      setMobileProfileOpen(false);
                    }}
                  />
                )}
                <div
                  onClick={() => {
                    navigate("/profile");
                    setMobileProfileOpen(false);
                  }}
                  className="cursor-pointer"
                >
                  <h4 className="font-bold text-lg">{displayName}</h4>
                  <p className="text-sm text-gray-600">+91 {displayPhone}</p>
                </div>
              </div>

              <button
                onClick={() => setMobileProfileOpen(false)}
                className="p-2 bg-gray-100 rounded-full"
              >
                <CloseIcon />
              </button>
            </div>

            {/* Profile menu: My Profile, Help & FAQ, Privacy Policy, Logout */}
            <div className="p-4 space-y-2">
              <button
                onClick={() => {
                  navigate("/profile");
                  setMobileProfileOpen(false);
                }}
                className="w-full text-left py-3 px-4 hover:bg-gray-100 rounded-lg flex items-center gap-3"
              >
                <PersonIcon /> My Profile
              </button>

              <button
                onClick={() => {
                  setShowHelpPopup(true);
                  setMobileProfileOpen(false);
                }}
                className="w-full text-left py-3 px-4 hover:bg-gray-100 rounded-lg flex items-center gap-3"
              >
                <HelpOutlineIcon /> Help & FAQ
              </button>

              <button
                onClick={() => {
                  navigate("/privacy-policy");
                  setMobileProfileOpen(false);
                }}
                className="w-full text-left py-3 px-4 hover:bg-gray-100 rounded-lg flex items-center gap-3"
              >
                <PrivacyTipOutlinedIcon /> Privacy Policy
              </button>

              <button
                onClick={handleLogout}
                className="w-full text-left py-3 px-4 hover:bg-red-50 text-red-600 font-bold rounded-lg flex items-center gap-3"
              >
                <LogoutIcon /> Logout
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Popups */}
      <CartPopup
        open={openCart}
        onClose={() => setOpenCart(false)}
        cartItems={cart}
        setCart={setCart}
      />

      {showHelpPopup && (
        <div
          className="fixed inset-0 bg-black/60 z-[9999] flex items-center justify-center"
          onClick={() => setShowHelpPopup(false)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <HelpFAQ embedded onBack={() => setShowHelpPopup(false)} />
          </motion.div>
        </div>
      )}
    </>
  );
}
